(() => {
  angular.module('app').factory('expiredGuaranteeLetterFlow', ExpiredGuaranteeLetterFlowFactory);

  ExpiredGuaranteeLetterFlowFactory.$inject = [
    '$uibModal',
    '$state',
    'abp.services.app.hospitalPanelServiceRequest',
    'Hms.GuaranteeLetters.GuaranteeLetterStatus',
    'Hms.ServiceRequests.ServiceRequestType',
    'moment',
  ];

  /**
   * The expired inpatient guarantee letter flow (HM-11144).
   *
   * Past its check-in validity window an inpatient GL cannot be checked in and cannot be
   * rescheduled — the hospital's only route forward is to cancel it and apply again. This factory
   * owns both halves of that conversation so the guarantee letter list, the patient card and the
   * hub patient card all behave identically:
   *
   *   1. the expired message, offering Close or Cancel Guarantee Letter
   *   2. after a successful cancellation, offering Done or Create New Guarantee Letter
   *
   * Cancellation itself reuses the existing cancellation modal and endpoint; nothing about the
   * cancellation contract changes here.
   */
  function ExpiredGuaranteeLetterFlowFactory(
    $uibModal,
    $state,
    hospitalPanelServiceRequestSvc,
    enumGuaranteeLetterStatus,
    enumServiceRequestType,
    moment
  ) {
    return {
      isExpired,
      prompt,
    };

    /**
     * Whether a guarantee letter is past its check-in validity window.
     *
     * The nightly expiry job is what sets the Expired status, so a letter can be past its
     * check-in expiry for a few hours before the status catches up. Both count as expired —
     * otherwise the hospital would be sent into a check-in the server is about to refuse.
     *
     * @param {object} guaranteeLetter with `status` and `checkInExpiryTime`.
     */
    function isExpired(guaranteeLetter) {
      if (!guaranteeLetter) return false;

      if (guaranteeLetter.status === enumGuaranteeLetterStatus.Expired.id) return true;

      return !!guaranteeLetter.checkInExpiryTime
        && moment().isAfter(moment(guaranteeLetter.checkInExpiryTime));
    }

    /**
     * Show the expired message and, if the hospital chooses to, take them through cancellation
     * and the offer to reapply.
     *
     * @param {object} options
     * @param {string} options.letterNumber the expired guarantee letter.
     * @param {Date|string} options.scheduledAdmissionTime quoted back in the message.
     * @param {object} options.reapplyParams state params for a new application — employeeId and
     *   panelLocationId, plus the hub identifiers when the letter belongs to a hub case. The
     *   scheduled admission date is deliberately not carried over; it must be re-entered.
     * @param {function} [options.onCancelled] called when the user stays on the page after a
     *   successful cancellation (Done / dismiss), so the caller can refresh its listing. Not
     *   called when they continue to Create New — that navigates away.
     */
    function prompt(options) {
      return $uibModal
        .open({
          templateUrl: require('./expiredGuaranteeLetter.modal.html'),
          controllerAs: 'vm',
          backdrop: 'static',
          controller: [
            '$uibModalInstance',
            function ExpiredController($uibModalInstance) {
              const vm = this;
              vm.message = App.localize(
                'CheckInGuaranteeLetterExpired',
                moment(options.scheduledAdmissionTime).format('DD/MM/YYYY')
              );
              vm.close = () => $uibModalInstance.dismiss();
              vm.cancelGuaranteeLetter = () => $uibModalInstance.close();
            },
          ],
        })
        .result.then(() => showCancellationModal(options));
    }

    function showCancellationModal(options) {
      return $uibModal
        .open({
          templateUrl: require('../cancellationModal/cancel.modal.html'),
          controller: 'clinic.views.hospital.common.cancelModal as vm',
          backdrop: 'static',
          size: 'lg',
          resolve: {
            letterRequestType() {
              return enumServiceRequestType.Initial.id;
            },
            letterStatus() {
              return enumGuaranteeLetterStatus.Expired.id;
            },
          },
        })
        .result.then((cancellationReason) => {
          // The shared modal never resolves without a reason today; kept as a cheap defence
          // because that contract lives in another file.
          if (!cancellationReason) return;

          // The cancel modal has already closed itself, and the reapply offer only appears once
          // the backend confirms the cancellation — so hold the whole screen busy for the gap.
          // On failure the overlay lifts and the ABP error handler surfaces the message; the
          // letter is left as it was.

          abp.ui.setBusy();

          hospitalPanelServiceRequestSvc
            .cancelGuaranteeLetterServiceRequest({
              letterNumber: options.letterNumber,
              cancellationReason,
            })
            .success(() => {
              showReapplyModal(options);
            })
            .finally(() => {
              abp.ui.clearBusy();
            });
        });
    }

    function showReapplyModal(options) {
      return $uibModal
        .open({
          templateUrl: require('./reapplyGuaranteeLetter.modal.html'),
          controllerAs: 'vm',
          backdrop: 'static',
          controller: [
            '$uibModalInstance',
            function ReapplyController($uibModalInstance) {
              const vm = this;
              vm.close = () => $uibModalInstance.dismiss();
              vm.createNew = () => $uibModalInstance.close();
            },
          ],
        })
        .result.then(
          () =>
            // Reapply always sources from the letter just cancelled, so the prefill parameter
            // is stamped here rather than by every caller.
            $state.go(
              'clinic.iglRequest',
              angular.extend({ sourceLetterNumber: options.letterNumber }, options.reapplyParams)
            ),
          () => {
            // Staying on the page (Done / dismiss) is the only path that needs the listing
            // refreshed — Create New navigates away and the target page loads fresh.
            if (options.onCancelled) options.onCancelled();
          }
        );
    }
  }
})();
